import { Directive, ElementRef, inject, Input, OnInit, signal } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
@Directive({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'hlm-error',
  host: {
    '[class.hidden]': '!errorMessage()',
    '[innerHTML]': 'errorMessage()',
    class: 'block text-error-500 text-sm',
  },
})
export class HlmErrorDirective implements OnInit {
  @Input() control: string;

  private readonly formGroupDirective = inject(FormGroupDirective);
  private readonly elementRef = inject(ElementRef);

  private parentFormGroup = signal<FormGroupDirective | null>(null);
  protected errorMessage = signal<string | null>(null);
  private isFormSubmitted = signal<boolean>(false);

  ngOnInit(): void {
    this.parentFormGroup.set(this.formGroupDirective);

    if (!this.parentFormGroup()) {
      console.warn('HlmErrorDirective: No parent FormGroupDirective found.');
      return;
    }

    this.parentFormGroup()!.ngSubmit.subscribe(() => {
      this.isFormSubmitted.set(true);
      this.updateErrorMessage();
    });

    const formControl = this.getFormControl();
    const inputElement = this.getInputElement();

    if (formControl) {
      formControl.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
        if (formControl.invalid) {
          formControl.markAsTouched();
          this.updateErrorMessage();
        }
      });

      formControl.statusChanges.pipe(distinctUntilChanged()).subscribe(() => {
        if (this.isFormSubmitted() || formControl.touched) this.updateErrorMessage();
      });

      if (inputElement) {
        inputElement.addEventListener('blur', () => {
          formControl.markAsTouched();
          this.updateErrorMessage();
        });
      }
    }
  }

  private getFormControl() {
    return this.parentFormGroup()?.form.get(this.control);
  }

  private getInputElement(): HTMLInputElement | null {
    if (!this.control) return null;
    // const formElement = this.elementRef.nativeElement.closest('form');
    // return formElement?.querySelector(`[formControlName="${this.control}"]`) || null;
    return document.getElementById(this.control) as HTMLInputElement | null;
  }

  private formatControlName(control: string): string {
    const controlName = control.replace(/([A-Z])/g, ' $1').trim();
    return controlName.toLowerCase();
  }

  private getErrorMessage(formControl: any): string | null {
    if (formControl.errors) {
      if (formControl.errors['required']) return `The ${this.formatControlName(this.control)} field is required`;
      if (formControl.errors['minlength']) return `Minimum length is ${formControl.errors['minlength'].requiredLength} characters`;
      if (formControl.errors['maxlength']) return `Maximum length is ${formControl.errors['maxlength'].requiredLength} characters`;
      if (formControl.errors['email']) return 'Please enter a valid email';
      if (formControl.errors['pattern']) return 'Please enter a valid value';
      if (formControl.errors['min']) return `Value must be greater than or equal to ${formControl.errors['min'].min}`;
      if (formControl.errors['max']) return `Value must be less than or equal to ${formControl.errors['max'].max}`;
    }

    return null;
  }

  private updateErrorMessage() {
    const formControl = this.getFormControl();
    if (formControl) this.errorMessage.set(this.getErrorMessage(formControl));
  }
}
